import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileSpreadsheet,
  RefreshCw,
  Loader2,
  Clock,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";

function fmtDT(iso) {
  if (!iso) return "Henüz yok";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * Compact dashboard card for the hourly Smartsheet sales-pipeline sync.
 * Only super_admins see this — the full-fat controls live on
 * /app/settings under "Smartsheet Entegrasyonu".
 */
export default function SmartsheetSyncCard() {
  const { user } = useAuthStore();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/smartsheet/status");
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!["super_admin", "ultra_admin"].includes(user?.role)) {
      setLoading(false);
      return;
    }
    refresh();
  }, [user?.role]);

  // Non-super_admins never see this card.
  if (!["super_admin", "ultra_admin"].includes(user?.role)) return null;
  // Hide until loaded — avoids a skeleton flash on accounts without Smartsheet.
  if (loading) return null;
  // Only show when THIS tenant actually configured/used Smartsheet: a saved
  // per-tenant token, a completed sync, or imported deals. Env fallbacks
  // alone must NOT surface the card on fresh/demo accounts.
  const st = status?.state || {};
  const tenantConfigured =
    st.api_token_set ||
    !!st.last_sync_at ||
    (status?.imported_deals ?? 0) > 0 ||
    (st.sheets || []).some((s) => s.last_sync_at);
  if (!status?.token_configured || !st.sheet_ref || !tenantConfigured) return null;

  const state = status?.state || {};
  const report = state.last_sync_report;

  const onQuickSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/smartsheet/sync", { sheet_ref: state.sheet_ref });
      const r = data.report || {};
      toast.success(`Sync tamam · ${r.deals_created ?? 0} yeni, ${r.deals_updated ?? 0} güncellenen`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync başarısız");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <section
      data-testid="smartsheet-sync-card"
      className="rounded-2xl border border-zinc-200 bg-gradient-to-br from-white to-emerald-50/40 p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-100 to-sky-100">
            <FileSpreadsheet className="h-4 w-4 text-emerald-700" />
          </div>
          <div>
            <h3 className="text-[14px] font-semibold text-zinc-950">Smartsheet Pipeline</h3>
            <p className="text-[11px] text-zinc-500">
              {loading
                ? "Yükleniyor…"
                : state.sheet_name
                  ? state.sheet_name
                  : "Sheet henüz bağlanmadı"}
            </p>
          </div>
        </div>
        <Link
          to="/app/settings"
          data-testid="smartsheet-card-settings-link"
          className="text-[11px] font-semibold text-violet-700 hover:text-violet-900"
        >
          Ayarlar →
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
        </div>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <MiniStat
              icon={Clock}
              label="Son sync"
              value={fmtDT(state.last_sync_at)}
              small
            />
            <MiniStat
              icon={RefreshCw}
              label="Deal"
              value={status?.imported_deals ?? 0}
            />
            <MiniStat
              icon={FileSpreadsheet}
              label="Auto"
              value={state.auto_sync_enabled ? "Açık" : "Kapalı"}
              tone={state.auto_sync_enabled ? "emerald" : "zinc"}
            />
          </div>

          {report && (
            <div className="mt-3 rounded-lg bg-white p-2 text-[11px] text-zinc-700 ring-1 ring-zinc-100">
              Son çalışma: <strong>{report.deals_created}</strong> yeni ·{" "}
              <strong>{report.deals_updated}</strong> güncellendi ·{" "}
              <strong>{report.total_rows}</strong> satır tarandı
            </div>
          )}

          {state.last_error && (
            <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-rose-50 p-2 text-[11px] text-rose-900 ring-1 ring-rose-200">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span className="truncate" title={state.last_error}>
                {state.last_error}
              </span>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              data-testid="smartsheet-card-sync-btn"
              onClick={onQuickSync}
              disabled={syncing}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-[12px] font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
            >
              {syncing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Şimdi sync
            </button>
            <Link
              to="/app/cop/deals"
              data-testid="smartsheet-card-deals-link"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-700 hover:text-violet-700"
            >
              Deal&apos;lara git <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </>
      )}
    </section>
  );
}

function MiniStat({ icon: Icon, label, value, tone, small }) {
  const toneClasses =
    tone === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : "border-zinc-200 bg-white text-zinc-900";
  return (
    <div className={`rounded-lg border p-1.5 ${toneClasses}`}>
      <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider opacity-70">
        <Icon className="h-2.5 w-2.5" /> {label}
      </div>
      <div className={`mt-0.5 font-semibold tabular-nums ${small ? "text-[11px]" : "text-[13px]"}`}>
        {value}
      </div>
    </div>
  );
}
